import { useState, useCallback } from 'react';
import {
    fetchEmployeesFromDb,
    updateEmployeeProfileInDb,
    addPickerRoleInDb,
    addDriverRoleInDb,
    removePickerRoleInDb,
    removeDriverRoleInDb,
    type Employee,
    type AddPickerRolePayload,
    type AddDriverRolePayload,
} from '../services/employeeService';
import { getErrorMessage } from '../utils/errors';

export function useEmployees() {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchEmployees = useCallback(async (): Promise<Employee[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchEmployeesFromDb();
            setEmployees(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch employees'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const updateEmployeeProfile = useCallback(async (
        authId: string,
        patch: { first_name?: string; last_name?: string; cellphone?: string }
    ): Promise<boolean> => {
        setError(null);
        try {
            await updateEmployeeProfileInDb(authId, patch);
            setEmployees(prev => prev.map(e =>
                e.auth_id === authId ? { ...e, ...patch } : e
            ));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update employee'));
            return false;
        }
    }, []);

    const addPickerRole = useCallback(async (payload: AddPickerRolePayload): Promise<boolean> => {
        setError(null);
        try {
            await addPickerRoleInDb(payload);
            await fetchEmployeesFromDb().then(setEmployees);
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to add picker role'));
            return false;
        }
    }, []);

    const addDriverRole = useCallback(async (payload: AddDriverRolePayload): Promise<boolean> => {
        setError(null);
        try {
            await addDriverRoleInDb(payload);
            await fetchEmployeesFromDb().then(setEmployees);
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to add driver role'));
            return false;
        }
    }, []);

    const removePickerRole = useCallback(async (pickerId: string): Promise<boolean> => {
        setError(null);
        try {
            await removePickerRoleInDb(pickerId);
            await fetchEmployeesFromDb().then(setEmployees);
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to remove picker role'));
            return false;
        }
    }, []);

    const removeDriverRole = useCallback(async (driverId: string): Promise<boolean> => {
        setError(null);
        try {
            await removeDriverRoleInDb(driverId);
            await fetchEmployeesFromDb().then(setEmployees);
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to remove driver role'));
            return false;
        }
    }, []);

    return {
        employees,
        loading,
        error,
        fetchEmployees,
        updateEmployeeProfile,
        addPickerRole,
        addDriverRole,
        removePickerRole,
        removeDriverRole,
    };
}
